/**
 * The phone's outbound queue (W10b, US-LV-18) — everything about it that is a DECISION rather than
 * socket wiring, so it can be tested without a DOM this app does not have.
 *
 * Why a queue at all, when `send()` already checks `readyState === OPEN`: the failure this exists
 * for is the venue whose access point is still associated and whose uplink is dead. `readyState`
 * says OPEN, `navigator.onLine` says `true`, TCP retransmits for tens of seconds, and the frame
 * dies in a buffer with nothing on the wire to correct it. At tap time the client is not offline by
 * any test it can run — so every submission is queued, INCLUDING while the socket looks fine.
 */
import type { ClientMessage } from "./useRoom.js";

/** A submission frame as it goes on the wire, with the round it was made in stamped on. */
export type Queued = Exclude<ClientMessage, { t: "join" }> & { qid: string };

export interface Pending {
  frame: Queued;
  /**
   * Was this frame ever written to a socket?
   *
   * The one thing that lets the screen keep AC5's promise. Items are not removed on drain (a
   * refusal has to stay recoverable), so a non-empty queue is the normal state of a round — and
   * "you have 3 answers waiting" or "the round closed before your connection came back" would
   * otherwise be said about answers that landed, on every single round. `false` is the only state
   * that may claim anything is unsent.
   *
   * Its known ceiling: a write to a socket that CLAIMED to be open is all this can observe, so in
   * the AP-up/uplink-down window it reads `true` for a frame that never arrived. That is exactly
   * AC5's stated residual, and killing it needs the deferred `ack` event.
   */
  sent: boolean;
}

/**
 * How many queued items one round may hold.
 *
 * It counts UNSENT items only, and that distinction is the whole thing: a sent item is replay
 * insurance, not a pending answer, so a Q&A participant on perfect wifi who upvotes eight entries
 * must not find their ninth action refused with "espere a conexão voltar". Refuse-the-new when eight
 * answers are genuinely waiting, never drop-oldest and never drop-newest: a silently discarded
 * sentence is the failure this story exists to end.
 *
 * Sized well inside `SUBMIT_LIMIT = 40/10 s` (per token), so a full drain never rate-limits itself,
 * and small enough to bound the replay fan-out — every accepted submission broadcasts an aggregate
 * to every socket, and this cap is the only mitigation for that in this wave.
 */
export const PENDING_MAX = 8;

/** The six submission tags. `join` is the credential (`onOpen` re-sends it) and is never queued. */
const SUBMIT_TAGS = new Set(["submit_mc", "submit_words", "submit_open", "upvote", "submit_prompt", "vote_prompt"]);

/**
 * Tags whose server semantics are last-write-wins / replace, so a newer one SUPERSEDES the queued
 * one instead of appending. Exact, not lossy: the room keeps `mc` as a `set` per token and replaces
 * a `submit_prompt` during the write phase. It is also what stops the priority answer — which sends
 * on every ± tap — from filling the queue that then has to refuse somebody's word.
 */
const SUPERSEDES = new Set(["submit_mc", "submit_prompt"]);

/** The two tags whose server semantics APPEND, and therefore the only two that carry a nonce. */
export function needsNonce(t: string): boolean {
  return t === "submit_words" || t === "submit_open";
}

/**
 * A per-submission nonce, minted ONCE at the tap and stored inside the queued frame — a replay that
 * re-minted it would be a new answer, and tests of the dedupe would pass while the product was broken.
 *
 * `crypto.randomUUID` needs a secure context; the fallback keeps a phone on an insecure origin able
 * to submit at all. Uniqueness only has to hold within one round of one room.
 */
export function newNonce(): string {
  const c = globalThis.crypto;
  return typeof c?.randomUUID === "function"
    ? c.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Bound the queue: at most `PENDING_MAX` in each half.
 *
 * The two halves are not the same kind of thing, so they are not trimmed the same way. UNSENT items
 * are the answers — the oldest are kept, which is the same FIFO rule the refusal at the cap
 * enforces. SENT items are only replay insurance for a frame that may have died in a socket that
 * claimed to be open, so the newest are kept and an older delivered one may be dropped. **An
 * undelivered answer is never dropped here.**
 */
export function bound(queue: Pending[]): Pending[] {
  // ponytail: O(n²) over a list capped at 2×8 — order has to survive, and a pair of index sets to
  // do it in one pass would be longer than the thing it optimises.
  const keepUnsent = queue.filter((p) => !p.sent).slice(0, PENDING_MAX);
  const keepSent = queue.filter((p) => p.sent).slice(-PENDING_MAX);
  return queue.filter((p) => (p.sent ? keepSent.includes(p) : keepUnsent.includes(p)));
}

/**
 * Add a submission. `full` is the local refusal — eight answers are already waiting — and the queue
 * comes back UNCHANGED with it, so the caller can keep the typed text and show a sentence rather
 * than the queue quietly losing an item.
 */
export function enqueue(queue: Pending[], frame: Queued, sent: boolean): { queue: Pending[]; full: boolean } {
  const item: Pending = { frame, sent };
  const at = SUPERSEDES.has(frame.t) ? queue.findIndex((p) => p.frame.t === frame.t) : -1;
  if (at >= 0) return { queue: bound(queue.map((p, i) => (i === at ? item : p))), full: false };
  if (!sent && unsentCount(queue) >= PENDING_MAX) return { queue, full: true };
  return { queue: bound([...queue, item]), full: false };
}

/** Everything has now been written to a socket. Called once per drain, on the whole queue. */
export function afterDrain(queue: Pending[]): Pending[] {
  if (queue.every((p) => p.sent)) return queue;
  return bound(queue.map((p) => (p.sent ? p : { ...p, sent: true })));
}

/** How many queued answers are not out yet — the only number the screen may put in a sentence. */
export function unsentCount(queue: readonly Pending[]): number {
  return queue.reduce((n, p) => (p.sent ? n : n + 1), 0);
}

// The tokenizer twin of `countWords` in the API's `live/wordcloud.ts`, which is what the server's
// `maxWords` cap actually counts (US-LV-20 AC1). Two lines, deliberately duplicated: the two apps
// share no live-domain package (`@klapzo/kit-core` is the Kit's), and adding one for a split and a
// length test would be a package built for two lines. Both sides are pinned to the same vectors by
// their own tests — "azul-escuro" is two words in both, a stray "a" is a word in neither, and
// stop-words count in neither, because a stop-word-aware cap is a hole rather than a kindness.
const TOKEN_SPLIT = /[^\p{L}\p{N}]+/u;
const MIN_WORD_LENGTH = 2;

/**
 * How many words this phone has already put in the round's cloud — the numerator of the "restam n
 * de 3" line (US-LV-20 AC1).
 *
 * Read off the QUEUE rather than from a counter of its own, because the queue is already the
 * phone's record of what it submitted for THIS round: items are never removed on drain, it is
 * persisted, and it is emptied when the round changes — so the count survives a reload and cannot
 * outlive its round.
 *
 * Its declared imprecision, and the reason the input is never DISABLED on the strength of it: a
 * refused frame is forgotten by `dropRefused`, but only by identifying the LAST one sent, so two
 * frames inside one round-trip can leave this reading the wrong one's words. It is a hint next to
 * an authoritative server refusal — exactly what `remainingBudget` is for a priority round — never
 * a second gate. Blocking on a number that can be wrong would cost somebody a word the round would
 * have taken.
 */
export function wordsUsed(queue: readonly Pending[], qid: string | null): number {
  if (qid === null) return 0;
  return queue.reduce((n, p) => {
    if (p.frame.t !== "submit_words" || p.frame.qid !== qid) return n;
    return n + p.frame.text.split(TOKEN_SPLIT).filter((tok) => tok.length >= MIN_WORD_LENGTH).length;
  }, 0);
}

/**
 * Does this refusal bin the WHOLE queue, or only concern the item it answered?
 *
 * A dead seat is the one that does: replaying under a freshly minted token would attribute one
 * person's answers to another identity. Everything else — `REJECTED`, `RATE_LIMITED`,
 * `NOT_ELIGIBLE`, `WRONG_PHASE`, `ROOM_FULL` — is about ONE submission, and binning the round's
 * other seven on one refusal is the sin this wave exists to end. (`ROOM_FULL` on a RESTORED seat
 * does mean the seat is gone, and that is decided by `seatIsLost` on the screen that owns the seat,
 * which clears the queue with it.)
 *
 * The two STALE-ROUND codes are not here: `reduce` answers `WRONG_ROUND`/`ROUND_CLOSED` before this
 * is reached, because they need the sentence as well as the clear.
 */
export function clearFor(queue: Pending[], code: string): Pending[] {
  return code === "UNKNOWN_TOKEN" || code === "INVALID_JOIN" ? [] : queue;
}

/**
 * The three refusals that mean "this exact submission did not land, and nothing else changed".
 *
 * Deliberately NOT `REJECTED`: it is the generic, and it is also what an `upvote` gets — dropping
 * the last frame on one would forget an ACCEPTED word. `WRONG_ROUND`/`ROUND_CLOSED` are handled
 * before this (they bin the whole queue), and `UNKNOWN_TOKEN`/`INVALID_JOIN` are `clearFor`'s.
 *
 * These three are NOT exclusive to the two appending submissions, which is the round-2 review's
 * correction of a completeness claim this comment used to make: `submit_mc` and the two prompt-vote
 * messages reach `NOT_ELIGIBLE` through `canSubmit`, and `submit_prompt` forwards `EMPTY`/
 * `LOOKS_LIKE_PII` from its own gate. So the TAG is checked too — see `dropRefused`.
 */
const DROPS_ONE = new Set(["EMPTY", "LOOKS_LIKE_PII", "NOT_ELIGIBLE"]);

/**
 * Forget the submission the server just refused (round-1 review, item I).
 *
 * `wordsUsed` reads the queue as the phone's record of what it put in the cloud, and items are
 * never removed on drain — so a word refused as PII kept counting against `maxWords` and "Restam 2
 * de 3" became "Restam 1 de 3" for a word that never reached the wall. The person then loses a word
 * to a typo, which is the opposite of what a per-person cap is for.
 *
 * The frame is identified as THE LAST APPENDING ONE WRITTEN TO A SOCKET, because an `error` frame
 * carries no nonce and this story does not grow the wire to give it one. `sent` is what makes that
 * safe against the obvious race: a word typed while the refusal was in flight has not gone out yet,
 * so it is not the one forgotten. The TAG is what keeps it off the frames these codes can also
 * answer without anything being appendable — see `DROPS_ONE`.
 *
 * ponytail: its residual, and the reason `wordsUsed` stays a HINT beside the server's authoritative
 * refusal — two frames sent inside one round-trip, one accepted and one refused, and this forgets
 * whichever went out last. Both are the same person's words in the same round, it self-corrects at
 * the next round, and closing it properly needs the deferred `ack` event.
 */
export function dropRefused(queue: Pending[], code: string): Pending[] {
  if (!DROPS_ONE.has(code)) return queue;
  for (let i = queue.length - 1; i >= 0; i -= 1) {
    const p = queue[i]!;
    // Only the two APPENDING tags, and only sent ones (round-2 review, item P). These codes also
    // answer an `mc` or a prompt-vote submission, and those SUPERSEDE rather than append — there is
    // nothing to forget. Dropping by position alone took whatever went out last, so a queued
    // upvote could be forgotten in place of the Q&A entry the server actually refused, and the
    // upvote's replay was lost for nothing.
    if (p.sent && needsNonce(p.frame.t)) return queue.filter((_, j) => j !== i);
  }
  return queue;
}

/**
 * What the phone says about its queue: an i18n key + the count it interpolates, or null for "say
 * nothing", which leaves today's `participant.submitted` exactly as it was (AC5 — while connected
 * the client genuinely cannot distinguish landed from buffered, so it does not pretend to).
 */
export function queueBanner(
  connected: boolean,
  offline: boolean,
  queue: readonly Pending[],
): { key: string; n: number } | null {
  const unsent = unsentCount(queue);
  // First, because it is the one state that changes what the next tap DOES: the round will refuse
  // it. UNSENT, like the cap itself — eight delivered answers are eight delivered answers.
  if (unsent >= PENDING_MAX) return { key: "participant.queueFull", n: unsent };
  // The only thing a participant behind a blocked WebSocket upgrade can act on.
  if (offline) return { key: "participant.offlineHint", n: unsent };
  if (!connected && unsent > 0) {
    return { key: unsent === 1 ? "participant.queuedOne" : "participant.queued", n: unsent };
  }
  return null;
}

/**
 * Keyed by the room's connect path, in the store the SEAT is in: this queue belongs to this room and
 * — in a live workshop — to this tab, because a closed tab is a person who left and a shared laptop
 * must not replay one room's answers into the next.
 *
 * Round-1 review: the store follows the seat. W11 moved a long-running seat to `localStorage` and
 * left the queue in `sessionStorage`, so an answer typed on Monday behind a dead uplink died with
 * the tab while the seat it was made under survived to Wednesday — the one combination where the
 * phone keeps the credential and loses the answer. A week-old replay is then refused HONESTLY by
 * `WRONG_ROUND`/`ROUND_CLOSED`, which is a sentence, rather than disappearing.
 *
 * Its ceiling, stated: with no service worker a reload DURING the outage cannot fetch the app shell
 * at all, so persistence only pays for a reload after recovery — or, now, for a tab closed and
 * reopened inside a still-open multi-day round.
 */
const KEY = "klapzo.q.";

/**
 * The store this queue lives in — the SAME rule the seat follows (`Participant.tsx`'s `storage`).
 *
 * Exported so the selection is testable without a DOM: this app has no jsdom, and "which store"
 * is the whole decision. Every access is guarded because either store is absent under vitest and
 * THROWS on access in a browser with site data blocked.
 */
export function queueStorage(persistent = false): Storage | null {
  try {
    if (persistent) return typeof localStorage === "undefined" ? null : localStorage;
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Read the queue back from EITHER store — session first, then local.
 *
 * Both, for the reason `readSeat` reads both: at mount the client has not resolved the room yet and
 * cannot know which kind it is, and a reader that had to be told would silently start every
 * long-running round with an empty queue.
 */
export function hydrate(connectPath: string): Pending[] {
  return hydrateFrom(connectPath, false) ?? hydrateFrom(connectPath, true) ?? [];
}

/** Null means "nothing usable HERE" — so the caller tries the other store, exactly as `readSeat` does. */
function hydrateFrom(connectPath: string, persistent: boolean): Pending[] | null {
  try {
    const raw = queueStorage(persistent)?.getItem(KEY + connectPath);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    // Same strictness as `readSeat`, and for a sharper reason: stored JSON is editable, and a frame
    // that lost its `qid` is one the round guard waves through into whatever round is open NOW.
    const kept = bound(parsed.filter(isPending));
    return kept.length > 0 ? kept : null;
  } catch {
    return null;
  }
}

function isPending(value: unknown): value is Pending {
  if (typeof value !== "object" || value === null) return false;
  const frame = (value as { frame?: unknown }).frame;
  if (typeof frame !== "object" || frame === null) return false;
  const { t, qid } = frame as { t?: unknown; qid?: unknown };
  return typeof t === "string" && SUBMIT_TAGS.has(t) && typeof qid === "string" && qid.length > 0;
}

/**
 * Write the queue to the store this room dictates, and clear it from the OTHER one.
 *
 * Clearing both is what keeps one copy: a phone that joined a live room and later a long-running one
 * on the same device would otherwise leave a stale queue in the store `hydrate` reads first, and
 * `clearQueue` at seat loss has to erase the frames wherever they were written — they were minted
 * under a token this browser is giving up.
 */
export function persist(connectPath: string, queue: Pending[], persistent = false): void {
  for (const store of [false, true]) {
    try {
      if (store !== persistent || queue.length === 0) queueStorage(store)?.removeItem(KEY + connectPath);
      else queueStorage(store)?.setItem(KEY + connectPath, JSON.stringify(queue));
    } catch {
      // Quota, or a browser refusing site data. Losing the reload guarantee is not worth losing the answer.
    }
  }
}
